import { create } from 'zustand';
import { Repository, OrganizationInfo, Activity } from '../types';

interface RepositoryState {
  repositories: Repository[];
  organizationInfo: OrganizationInfo | null;
  activityLog: Activity[];
  activityLoading: boolean;
  hasMoreActivity: boolean;
  loading: boolean;
  initialLoad: boolean;
  currentInstallation: number | null;

  // Actions
  setCurrentInstallation: (installationId: number | null) => void;
  loadRepositories: () => Promise<void>;
  loadActivityHistory: () => Promise<void>;
  loadMoreActivity: () => Promise<void>;
  freezeRepository: (
    repo: Repository
  ) => Promise<{ success: boolean; error?: string; requiresUpgrade?: boolean; message?: string }>;
  unfreezeRepository: (repo: Repository) => Promise<{ success: boolean; error?: string }>;
  freezeBranch: (
    owner: string,
    repo: string,
    branch: string
  ) => Promise<{ success: boolean; error?: string }>;
  unfreezeBranch: (
    owner: string,
    repo: string,
    branch: string
  ) => Promise<{ success: boolean; error?: string }>;
  bulkFreezeRepositories: (repos: Repository[]) => Promise<any>;
  bulkUnfreezeRepositories: (repos: Repository[]) => Promise<any>;
  updateExemptions: (
    owner: string,
    repo: string,
    exemptPRs: string[],
    branch?: string
  ) => Promise<{ success: boolean; error?: string }>;
}

export const useRepositoryStore = create<RepositoryState>((set, get) => ({
  repositories: [],
  organizationInfo: null,
  activityLog: [],
  activityLoading: false,
  hasMoreActivity: true,
  loading: false,
  initialLoad: true,
  currentInstallation: null,

  setCurrentInstallation: installationId => {
    set({ currentInstallation: installationId });
  },

  loadRepositories: async () => {
    const { currentInstallation } = get();
    if (!currentInstallation) {
      return;
    }

    try {
      set({ loading: true });

      const response = await fetch('/api/v1/repos', {
        credentials: 'same-origin',
      });

      const data = await response.json();

      if (data.organization) {
        set({ organizationInfo: data.organization });
      }

      const repos = data.repos || data.repositories || [];
      // Preserve loading states when updating repository data
      set(state => ({
        repositories: repos.map((repo: Repository) => {
          const existing = state.repositories.find(
            r => r.owner === repo.owner && r.repo === repo.repo
          );
          return {
            ...repo,
            freezing: existing?.freezing || false,
            unfreezing: existing?.unfreezing || false,
          };
        }),
      }));
    } catch (error) {
      console.error('Error loading repositories:', error);
      set({ repositories: [] });
    } finally {
      set({ loading: false, initialLoad: false });
    }
  },

  loadActivityHistory: async () => {
    const { currentInstallation } = get();
    if (!currentInstallation) {
      return;
    }

    try {
      const response = await fetch('/api/v1/repos/history?limit=30', {
        credentials: 'same-origin',
      });

      const data = await response.json();
      if (data.success && data.history) {
        const activities = data.history.map((entry: any) => ({
          type: entry.type,
          message: entry.message,
          timestamp: new Date(entry.timestamp),
          id: entry.id,
        }));
        set({
          activityLog: activities,
          hasMoreActivity: activities.length === 30,
        });
      }
    } catch (error) {
      console.error('Failed to load activity history:', error);
    }
  },

  loadMoreActivity: async () => {
    const { currentInstallation, activityLog, activityLoading, hasMoreActivity } = get();
    if (!currentInstallation || activityLoading || !hasMoreActivity) {
      return;
    }

    try {
      set({ activityLoading: true });

      // Get the oldest activity timestamp for pagination
      const oldestActivity = activityLog[activityLog.length - 1];
      const beforeTimestamp = oldestActivity?.timestamp
        ? new Date(oldestActivity.timestamp).toISOString()
        : undefined;

      const url = beforeTimestamp
        ? `/api/v1/repos/history?limit=30&before=${encodeURIComponent(beforeTimestamp)}`
        : '/api/v1/repos/history?limit=30';

      const response = await fetch(url, {
        credentials: 'same-origin',
      });

      const data = await response.json();
      if (data.success && data.history) {
        const newActivities = data.history.map((entry: any) => ({
          type: entry.type,
          message: entry.message,
          timestamp: new Date(entry.timestamp),
          id: entry.id,
        }));

        // Filter out duplicates and append new activities
        const existingIds = new Set(activityLog.map(a => a.id));
        const uniqueNewActivities = newActivities.filter((a: any) => !existingIds.has(a.id));

        set({
          activityLog: [...activityLog, ...uniqueNewActivities],
          hasMoreActivity: newActivities.length === 30,
        });
      }
    } catch (error) {
      console.error('Failed to load more activity:', error);
    } finally {
      set({ activityLoading: false });
    }
  },

  freezeRepository: async (repo: Repository) => {
    set(state => ({
      repositories: state.repositories.map(r =>
        r.owner === repo.owner && r.repo === repo.repo ? { ...r, freezing: true } : r
      ),
    }));

    try {
      const response = await fetch(`/api/v1/repos/${repo.owner}/${repo.repo}/freeze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      });

      const result = await response.json();

      if (result.success) {
        await get().loadRepositories();
        return { success: true };
      } else {
        return {
          success: false,
          error: result.error,
          requiresUpgrade: result.requiresUpgrade,
          message: result.message,
        };
      }
    } catch (error) {
      return { success: false, error: (error as Error).message };
    } finally {
      set(state => ({
        repositories: state.repositories.map(r =>
          r.owner === repo.owner && r.repo === repo.repo ? { ...r, freezing: false } : r
        ),
      }));
    }
  },

  unfreezeRepository: async (repo: Repository) => {
    set(state => ({
      repositories: state.repositories.map(r =>
        r.owner === repo.owner && r.repo === repo.repo ? { ...r, unfreezing: true } : r
      ),
    }));

    try {
      const response = await fetch(`/api/v1/repos/${repo.owner}/${repo.repo}/unfreeze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      });

      const result = await response.json();

      if (result.success) {
        await get().loadRepositories();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: (error as Error).message };
    } finally {
      set(state => ({
        repositories: state.repositories.map(r =>
          r.owner === repo.owner && r.repo === repo.repo ? { ...r, unfreezing: false } : r
        ),
      }));
    }
  },

  freezeBranch: async (owner: string, repo: string, branch: string) => {
    try {
      const response = await fetch(`/api/v1/repos/${owner}/${repo}/freeze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ branch }),
      });

      const result = await response.json();

      if (result.success) {
        await get().loadRepositories();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  },

  unfreezeBranch: async (owner: string, repo: string, branch: string) => {
    try {
      const response = await fetch(`/api/v1/repos/${owner}/${repo}/unfreeze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ branch }),
      });

      const result = await response.json();

      if (result.success) {
        await get().loadRepositories();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  },

  bulkFreezeRepositories: async (repos: Repository[]) => {
    // Set freezing state for selected repos only
    set(state => ({
      repositories: state.repositories.map(r => {
        const isSelected = repos.some(
          selected => selected.owner === r.owner && selected.repo === r.repo
        );
        return isSelected ? { ...r, freezing: true } : r;
      }),
    }));

    try {
      const response = await fetch('/api/v1/repos/freeze/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          repos: repos.map(r => ({ owner: r.owner, repo: r.repo })),
        }),
      });

      const result = await response.json();

      // Clear freezing state BEFORE reloading
      set(state => ({
        repositories: state.repositories.map(r => {
          const isSelected = repos.some(
            selected => selected.owner === r.owner && selected.repo === r.repo
          );
          return isSelected ? { ...r, freezing: false } : r;
        }),
      }));

      if (result.success) {
        await get().loadRepositories();
        return { success: true, summary: result.summary };
      } else {
        return {
          success: false,
          summary: result.summary,
          results: result.results,
          error: result.error,
        };
      }
    } catch (error) {
      // Clear freezing state on error
      set(state => ({
        repositories: state.repositories.map(r => {
          const isSelected = repos.some(
            selected => selected.owner === r.owner && selected.repo === r.repo
          );
          return isSelected ? { ...r, freezing: false } : r;
        }),
      }));
      return { success: false, error: (error as Error).message };
    }
  },

  bulkUnfreezeRepositories: async (repos: Repository[]) => {
    // Set unfreezing state for selected repos only
    set(state => ({
      repositories: state.repositories.map(r => {
        const isSelected = repos.some(
          selected => selected.owner === r.owner && selected.repo === r.repo
        );
        return isSelected ? { ...r, unfreezing: true } : r;
      }),
    }));

    try {
      const response = await fetch('/api/v1/repos/unfreeze/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          repos: repos.map(r => ({ owner: r.owner, repo: r.repo })),
        }),
      });

      const result = await response.json();

      // Clear unfreezing state BEFORE reloading
      set(state => ({
        repositories: state.repositories.map(r => {
          const isSelected = repos.some(
            selected => selected.owner === r.owner && selected.repo === r.repo
          );
          return isSelected ? { ...r, unfreezing: false } : r;
        }),
      }));

      if (result.success) {
        await get().loadRepositories();
        return { success: true, summary: result.summary };
      } else {
        return {
          success: false,
          summary: result.summary,
          results: result.results,
          error: result.error,
        };
      }
    } catch (error) {
      // Clear unfreezing state on error
      set(state => ({
        repositories: state.repositories.map(r => {
          const isSelected = repos.some(
            selected => selected.owner === r.owner && selected.repo === r.repo
          );
          return isSelected ? { ...r, unfreezing: false } : r;
        }),
      }));
      return { success: false, error: (error as Error).message };
    }
  },

  updateExemptions: async (owner: string, repo: string, exemptPRs: string[], branch?: string) => {
    try {
      const response = await fetch(`/api/v1/repos/${owner}/${repo}/exemptions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ exemptPRs, branch }),
      });

      const result = await response.json();

      if (result.success) {
        // Reload repositories to get updated branch data
        await get().loadRepositories();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  },
}));
